import React, { useState } from 'react';
import { Row, Col, Button, Badge, Card } from 'react-bootstrap';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faArrowLeft,
  faGripVertical,
  faEdit,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

import {
  useFetchSupportForm,
  useDeleteFormField,
  useReorderFormFields,
} from '../../../services/supportAdmin';
import PrivateLayout from '../../layouts/PrivateLayout';
import Panel from '../../common/Panel';
import Loading from '../../common/Loading';

import FieldEditorModal from './modals/FieldEditorModal';

const FIELD_TYPE_LABELS = {
  text: 'Text',
  textarea: 'Text Area',
  email: 'Email',
  select: 'Dropdown',
  file: 'File Upload',
  number: 'Number',
  date: 'Date',
  phone: 'Phone',
};

const SupportFormFieldsEditor = () => {
  const { formId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [fieldModal, setFieldModal] = useState(false);

  const { isLoading, data: formData } = useFetchSupportForm(formId);

  const deleteField = useDeleteFormField(
    () => {
      toast.success('Field deleted successfully.');
      queryClient.invalidateQueries(['supportForms', formId]);
    },
    { onError: (err) => toast.error(err.message) },
  );

  const reorderFields = useReorderFormFields(
    () => {
      queryClient.invalidateQueries(['supportForms', formId]);
    },
    { onError: (err) => toast.error(err.message) },
  );

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const fields = [...(formData?.fields || [])];
    const [removed] = fields.splice(result.source.index, 1);
    fields.splice(result.destination.index, 0, removed);

    // Create new order mapping
    const fieldOrders = fields.map((field, index) => ({
      fieldId: field.fieldId,
      fieldOrder: index + 1,
    }));

    reorderFields.mutate({ formId, fieldOrders });
  };

  if (isLoading) {
    return (
      <PrivateLayout>
        <Loading msg="Loading form..." />
      </PrivateLayout>
    );
  }

  if (!formData) {
    return (
      <PrivateLayout>
        <Panel>
          <p>Form not found.</p>
          <Button variant="link" onClick={() => navigate('/admin/support-forms')}>
            Back to Forms
          </Button>
        </Panel>
      </PrivateLayout>
    );
  }

  const fields = formData.fields || [];

  return (
    <PrivateLayout>
      <Row>
        <Col>
          <Panel>
            <Row className="align-items-center mb-4">
              <Col>
                <Link
                  to="/admin/support-forms"
                  className="text-decoration-none text-muted mb-2 d-inline-block"
                >
                  <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                  Back to Forms
                </Link>
                <h2 className="mb-1">{formData.formTitle}</h2>
                <p className="text-muted mb-0">
                  Manage form fields and their Monday.com column mappings.
                </p>
              </Col>
              <Col xs="auto">
                <Button
                  variant="outline-primary"
                  onClick={() => setFieldModal(true)}
                >
                  <FontAwesomeIcon icon={faPlus} /> Add Field
                </Button>
              </Col>
            </Row>

            {fields.length === 0 ? (
              <Card className="text-center py-5">
                <Card.Body>
                  <p className="text-muted mb-3">
                    No fields configured yet. Add fields to build your form.
                  </p>
                  <Button
                    variant="primary"
                    onClick={() => setFieldModal(true)}
                  >
                    <FontAwesomeIcon icon={faPlus} /> Add First Field
                  </Button>
                </Card.Body>
              </Card>
            ) : (
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="fields">
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      {fields.map((field, index) => (
                        <Draggable
                          key={field.fieldId}
                          draggableId={field.fieldId}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <Card
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`mb-2 ${snapshot.isDragging ? 'shadow-lg' : ''}`}
                            >
                              <Card.Body className="py-2">
                                <Row className="align-items-center">
                                  <Col xs="auto">
                                    <span
                                      {...provided.dragHandleProps}
                                      className="text-muted"
                                      style={{ cursor: 'grab' }}
                                    >
                                      <FontAwesomeIcon icon={faGripVertical} />
                                    </span>
                                  </Col>
                                  <Col>
                                    <strong>{field.fieldLabel}</strong>
                                    <br />
                                    <small className="text-muted">
                                      Key: {field.fieldKey}
                                    </small>
                                  </Col>
                                  <Col xs="auto">
                                    <Badge bg="secondary" className="me-2">
                                      {FIELD_TYPE_LABELS[field.fieldType] || field.fieldType}
                                    </Badge>
                                    {field.fieldRequired && (
                                      <Badge bg="warning" text="dark" className="me-2">
                                        Required
                                      </Badge>
                                    )}
                                    {field.mondayColumnId && (
                                      <Badge bg="info">
                                        Monday: {field.mondayColumnId}
                                      </Badge>
                                    )}
                                  </Col>
                                  <Col xs="auto">
                                    <Button
                                      variant="outline-secondary"
                                      size="sm"
                                      className="me-1"
                                      onClick={() => setFieldModal(field)}
                                    >
                                      <FontAwesomeIcon icon={faEdit} />
                                    </Button>
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      onClick={() => {
                                        if (
                                          window.confirm(
                                            `Delete field "${field.fieldLabel}"?`,
                                          )
                                        ) {
                                          deleteField.mutate({
                                            formId,
                                            fieldId: field.fieldId,
                                          });
                                        }
                                      }}
                                    >
                                      <FontAwesomeIcon icon={faTrash} />
                                    </Button>
                                  </Col>
                                </Row>
                              </Card.Body>
                            </Card>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </Panel>
        </Col>
      </Row>

      <FieldEditorModal
        show={fieldModal !== false}
        toggle={() => setFieldModal(false)}
        formId={formId}
        mondayBoardId={formData.mondayBoardId}
        field={fieldModal}
        onSuccess={() => queryClient.invalidateQueries(['supportForms', formId])}
      />
    </PrivateLayout>
  );
};

export default SupportFormFieldsEditor;
